import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { ChartColor, getChartGridColor } from '@t-one-internal/design-system';

interface TrendlineRow {
  userId: string | number;
  employeeName: string;
  cycleName: string;
  cycleStartDate: string;
  avgManagerRating: number;
}

interface TeamPerformanceTrendlineProps {
  data: Array<Record<string, unknown>>;
}

const COLORS = Object.values(ChartColor);

const toTrendlineRow = (raw: Record<string, unknown>): TrendlineRow => ({
  userId: (raw.userId as string | number) ?? '',
  employeeName: (raw.employeeName as string) ?? 'Unknown',
  cycleName: (raw.cycleName as string) ?? '',
  cycleStartDate: (raw.cycleStartDate as string) ?? '',
  avgManagerRating: Number(raw.avgManagerRating ?? 0),
});

const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ color: string; name: string; value: number; payload: Record<string, unknown> }> }) => {
  if (!active || !payload?.length) return null;
  const cycleName = payload[0]?.payload?.cycleName as string | undefined;
  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '10px 14px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        border: '1px solid #E5E7EB',
      }}
    >
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
        {cycleName}
      </div>
      {payload.map((entry) => (
        <div
          key={entry.name}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '2px 0',
            fontSize: '12px',
          }}
        >
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: entry.color,
              flexShrink: 0,
            }}
          />
          <span style={{ color: '#6B7280', flex: 1 }}>{entry.name}</span>
          <span style={{ color: '#111827', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {entry.value.toFixed(1)}
          </span>
        </div>
      ))}
    </div>
  );
};

export const TeamPerformanceTrendline: React.FC<TeamPerformanceTrendlineProps> = ({ data }) => {
  const { chartData, employeeNames } = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return { chartData: [], employeeNames: [] };
    }

    const rows = data.map(toTrendlineRow);

    // Extract unique cycles sorted by cycleStartDate ascending
    const cycleMap = new Map<string, string>();
    for (const row of rows) {
      if (!cycleMap.has(row.cycleName)) {
        cycleMap.set(row.cycleName, row.cycleStartDate);
      }
    }
    const sortedCycles = [...cycleMap.entries()]
      .sort(([nameA, dateA], [nameB, dateB]) => dateA.localeCompare(dateB) || nameA.localeCompare(nameB))
      .map(([name]) => name);

    // Extract unique employee names sorted alphabetically
    const names = [...new Set(rows.map((r) => r.employeeName))].sort();

    // Pivot into Recharts format with numeric index so XAxis type="number" works
    // This ensures the first data point sits exactly on the Y-axis
    const pivoted = sortedCycles.map((cycleName, i) => {
      const point: Record<string, unknown> = { cycleName, cycleIndex: i };
      for (const row of rows) {
        if (row.cycleName === cycleName && row.avgManagerRating > 0) {
          point[row.employeeName] = row.avgManagerRating;
        }
      }
      return point;
    });

    return { chartData: pivoted, employeeNames: names };
  }, [data]);

  if (chartData.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF', fontSize: '13px' }}>
        No performance data available
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 20 }}>
            <CartesianGrid stroke={getChartGridColor('axis')} strokeDasharray="none" vertical={false} />
            <XAxis
              dataKey="cycleIndex"
              type="number"
              domain={[0, chartData.length - 1]}
              ticks={chartData.map((_, i) => i)}
              tick={(props: { x: number; y: number; payload: { value: number } }) => {
                const idx = props.payload.value;
                const label = (chartData[idx]?.cycleName as string) ?? '';
                const compact = chartData.length > 5;

                // Compact mode: show dot marker with name on hover only
                if (compact) {
                  return (
                    <g>
                      <title>{label}</title>
                      <circle cx={props.x} cy={props.y + 10} r={3} fill="#9CA3AF" />
                    </g>
                  );
                }

                const isFirst = idx === 0;
                const isLast = idx === chartData.length - 1;
                const anchor = isFirst ? 'start' : isLast ? 'end' : 'middle';
                const maxChars = 16;
                const maxLines = 3;
                const words = label.split(' ');
                const allLines: string[] = [];
                let current = '';
                for (const word of words) {
                  if (current && (current + ' ' + word).length > maxChars) {
                    allLines.push(current);
                    current = word;
                  } else {
                    current = current ? current + ' ' + word : word;
                  }
                }
                if (current) allLines.push(current);
                const truncated = allLines.length > maxLines;
                const lines = allLines.slice(0, maxLines);
                if (truncated) {
                  lines[maxLines - 1] = lines[maxLines - 1].slice(0, maxChars - 3) + '...';
                }
                return (
                  <g>
                    <title>{label}</title>
                    <text x={props.x} y={props.y + 12} textAnchor={anchor} fill="#6B7280" fontSize={11}>
                      {lines.map((line, i) => (
                        <tspan key={i} x={props.x} dy={i === 0 ? 0 : 14}>
                          {line}
                        </tspan>
                      ))}
                    </text>
                  </g>
                );
              }}
              axisLine={{ stroke: '#D1D5DB' }}
              tickLine={false}
              interval={0}
            />
            <YAxis
              domain={[1, 5]}
              ticks={[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5]}
              tickFormatter={(v: number) => v.toFixed(1)}
              tick={{ fontSize: 12, fill: '#9CA3AF' }}
              axisLine={{ stroke: '#D1D5DB' }}
              tickLine={false}
              width={32}
            />
            <Tooltip content={<CustomTooltip />} cursor={false} />
            <Legend
              iconType="plainline"
              iconSize={20}
              wrapperStyle={{ fontSize: '12px', color: '#374151', paddingTop: '24px' }}
            />
            {employeeNames.map((name, idx) => {
              const color = COLORS[idx % COLORS.length];
              return (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stroke={color}
                  strokeWidth={2}
                  dot={{ r: 4, fill: color, stroke: color, strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: color, stroke: '#fff', strokeWidth: 2 }}
                  connectNulls
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
